import {
  coerceAgentOrderDraft,
  readAgentPickupDate,
} from '@hero/hooks-shared';
import { formatPickupWindow, getDeviceTimezone } from '@hero/core';
import { stagedPlaceLabels, type StagedPlace } from '@hero/agent';
import type { PendingConfirmation } from './types';

export interface StagedCreateOrderSummaryParts {
  orderCount: number;
  clientName: string;
  originLabel: string;
  destinationLabel: string;
  extraDestinationCount: number;
  pickupLabel: string;
}

const readLocationLabel = (point: StagedPlace): string =>
  stagedPlaceLabels(point).city ?? '';

/**
 * Build interpolation parts for a one-line staged-order summary when
 * the model stays silent after `prepare_create_order_draft`.
 */
export const buildStagedCreateOrderSummaryParts = (
  pending: PendingConfirmation,
): StagedCreateOrderSummaryParts | null => {
  if (pending.action !== 'create_order') return null;

  try {
    const draft = coerceAgentOrderDraft(pending.payload);
    const pickupDate = readAgentPickupDate(pending.payload);
    const timezone = getDeviceTimezone() ?? 'Asia/Riyadh';

    const clientName =
      draft.clientName?.trim() ||
      String(pending.payload.clientName ?? '').trim() ||
      'Internal';

    const originLabel = readLocationLabel(draft.origin ?? {});

    const resolvedDestinations = draft.destinations.filter(
      d => readLocationLabel(d.location ?? {}) !== '',
    );
    const firstDest = resolvedDestinations[0]?.location ?? {};
    const destinationLabel = readLocationLabel(firstDest);
    const extraDestinationCount = Math.max(0, resolvedDestinations.length - 1);

    const orderCount = draft.destinations.reduce(
      (sum, d) => sum + (d.vehicleQuantity ?? 1),
      0,
    );

    const pickupLabel = pickupDate
      ? formatPickupWindow(
          { from: pickupDate.from, to: pickupDate.to },
          timezone,
        )
      : '';

    return {
      orderCount: orderCount > 0 ? orderCount : 1,
      clientName,
      originLabel,
      destinationLabel,
      extraDestinationCount,
      pickupLabel,
    };
  } catch {
    return null;
  }
};
